// src/utils/customerMapper.ts
// Un cliente arriva dal DB come riga `Customers` (snake_case, con `full_name`
// e senza `first_name`/`last_name`): questa è l'unica conversione verso il tipo
// `Customer` usato da CustomerDetailModal e dalle card. Prima esisteva solo
// dentro Customers.tsx e chi apriva la scheda dall'agenda vedeva "Cliente senza nome".
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import type { Customer } from '../components/modals/CustomerDetailModal';

// Palette avatar come nel mock UI
const AVATAR_COLORS = [
  'bg-rose-100 text-rose-600',
  'bg-blue-100 text-blue-600',
  'bg-emerald-100 text-emerald-600',
  'bg-amber-100 text-amber-600',
  'bg-purple-100 text-purple-600',
];

export const getAvatarColor = (id: string | number) => {
  const str = String(id);
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash + str.charCodeAt(i) * 17) % 997;
  }
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

export const splitFullName = (raw: any): { firstName: string; lastName: string } => {
  const fullName =
    raw?.full_name || [raw?.first_name, raw?.last_name].filter(Boolean).join(' ') || '';
  const parts = String(fullName).split(' ').filter(Boolean);
  return {
    firstName: parts[0] || raw?.first_name || '',
    lastName: parts.slice(1).join(' ') || raw?.last_name || '',
  };
};

/**
 * @param raw riga della tabella `Customers`
 * @param bookings elenco prenotazioni (serve per totale visite e ultima visita)
 * @param neverVisitedLabel etichetta tradotta per chi non è mai venuto
 */
export const mapRawClientToCustomer = (
  raw: any,
  bookings: any[] | null | undefined,
  neverVisitedLabel: string
): Customer => {
  const { firstName, lastName } = splitFullName(raw);

  const clientBookings = (bookings || []).filter(
    (b: any) => b.client_id === raw.id && b.date
  );

  let lastVisit: string;
  if (clientBookings.length > 0) {
    const latest = clientBookings.reduce((acc: any, curr: any) =>
      acc.date > curr.date ? acc : curr
    );
    lastVisit = format(new Date(latest.date), 'dd MMM yyyy', { locale: it });
  } else if (raw.updated_at || raw.created_at) {
    lastVisit = format(new Date(raw.updated_at || raw.created_at), 'dd MMM yyyy', {
      locale: it,
    });
  } else {
    lastVisit = neverVisitedLabel;
  }

  return {
    id: String(raw.id),
    firstName,
    lastName,
    email: raw.email || '',
    phone: raw.phone || '',
    isEmailVerified: !raw.is_manually_entry && !!raw.email,
    totalAppointments: clientBookings.length,
    lastVisit,
    avatarColor: getAvatarColor(raw.id),
    notes: raw.notes || '',
    isManuallyEntry: raw.is_manually_entry,
    isBlocked: raw.is_blocked || false,
    avatar: raw.profiles?.avatar || raw.avatar,
    merged_into_id: raw.merged_into_id,
  };
};
